import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Controller } from 'react-hook-form';
import { FormControl, FormHelperText, FormLabel, OutlinedInput, makeStyles } from '@material-ui/core';
import { z } from 'zod';
import ChipList from '../Common/Form/ChipList';
import Form from '../Common/Form';
import { AUTOMATION_SPIDER_EXCLUSIONS } from '../../helpers/constants';

const validationSchema = () => {
  return z.object({
    spider_exclusion: z
      .enum(AUTOMATION_SPIDER_EXCLUSIONS)
      .nullable()
      .catch(null),
    reason: z
      .string()
      .trim()
      .nonempty(),
  });
};

const DEFAULT_FORM_VALUES = {
  spider_exclusion: '',
  reason: '',
};

const useStyles = makeStyles(theme => ({
  required: {
    color: theme.palette.type === 'light' ? '#C41212' : theme.palette.error.dark,
  },
  label: {
    marginBottom: theme.spacing(2),
    fontSize: '14px',
  },
  helperText: {
    fontSize: '14px',
    lineHeight: '1.2',
    marginTop: 0,
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(0.625),
  },
}));

const propTypes = {
  automationData: PropTypes.shape({
    spider_exclusion: PropTypes.string,
    reason: PropTypes.string,
  }),
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSuccess: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  eventMeta: PropTypes.object,
};
const defaultProps = {
  automationData: null,
  isError: false,
  isLoading: false,
  isSuccess: false,
  onReset: undefined,
  eventMeta: undefined,
};

function SpiderForm({ automationData, isError, isLoading, isSuccess, onSubmit, onReset, eventMeta }) {
  const classes = useStyles();

  // Memoize the values to prevent the form from resetting during re-renders.
  const initialValues = useMemo(() => {
    if (!automationData) {
      return DEFAULT_FORM_VALUES;
    }

    // eslint-disable-next-line camelcase
    const { spider_exclusion } = automationData;
    const initialObject = {
      spider_exclusion,
      reason: '',
    };

    return initialObject;
  }, [automationData]);

  return (
    <Form
      initialValues={initialValues}
      isError={isError}
      isLoading={isLoading}
      isSuccess={isSuccess}
      validationSchema={validationSchema(eventMeta.category)}
      values={initialValues}
      onSubmit={onSubmit}
      onReset={onReset}
    >
      {({ control }) => (
        <>
          <FormControl component="fieldset">
            <FormLabel component="legend" classes={{ root: classes.label }}>
              Please select the exclusion type. (Optional)
            </FormLabel>

            <Controller
              name="spider_exclusion"
              control={control}
              render={({ field }) => (
                <ChipList
                  disabled={field.disabled}
                  options={AUTOMATION_SPIDER_EXCLUSIONS.map(option => ({ label: option, value: option }))}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel classes={{ root: classes.label }}>
              Please provide a reason for this request.
              <span className={classes.required}>* </span>
              (Required)
            </FormLabel>
            <FormHelperText className={classes.helperText}>
              Previous reason:
              <br />
              {automationData?.reason}
            </FormHelperText>

            <Controller
              name="reason"
              control={control}
              render={({ field }) => <OutlinedInput placeholder="Type text here." rows={3} multiline {...field} />}
            />
          </FormControl>
        </>
      )}
    </Form>
  );
}

SpiderForm.propTypes = propTypes;
SpiderForm.defaultProps = defaultProps;

function mapStateToProps(state) {
  return {
    eventMeta: state.pricing.eventMeta,
  };
}

export default connect(mapStateToProps)(SpiderForm);
